import { USER_V2_PATH_SCREENS } from '@/components/user-v2/types';

interface UserV2ModalState {
  screen: string | null;
  props: Record<string, unknown>;
}

// Estado global do modal Minha Conta V2 (ENG-362). As rotas /user/* continuam
// existindo pra deep link, mas a navegação é interceptada pelo middleware
// userV2Modal e vira troca de tela DENTRO do modal — fechar não navega.
export default function useUserV2Modal() {
  const state = useState<UserV2ModalState>('user-v2-modal', () => ({
    screen: null,
    props: {},
  }));

  const route = useRoute();

  // Só troca o texto da barra de endereço (history.replaceState) — nunca
  // uma navegação de verdade, a página por trás do modal não muda. É só
  // pra deixar copiável o link da tela aberta no momento.
  // REPLACE (não push): cada troca de tela substitui a entrada do
  // histórico em vez de empilhar uma nova — botão voltar/avançar do
  // navegador não navega entre telas do modal (fora de escopo por ora).
  function syncAddressBar(path: string) {
    if (!import.meta.client || !path) {
      return;
    }

    window.history.replaceState(window.history.state, '', path);
  }

  // path: usado por openByPath, que já sabe o caminho exato acessado.
  // Sem ele, cai pro reverse-lookup de currentPath — ok pra telas com um
  // único path, mas ambíguo pra telas com mais de um (bets-history,
  // income-report), onde teria sempre o primeiro path sem props.
  function open(
    screen: string,
    props: Record<string, unknown> = {},
    path?: string,
  ) {
    state.value = { screen, props };

    syncAddressBar(path ?? currentPath.value);
  }

  function close() {
    state.value = { screen: null, props: {} };

    syncAddressBar(route.fullPath);
  }

  // Resolve pra qual screen um path (com ou sem query) abriria — usado tanto
  // por openByPath (decide o que abrir) quanto pelo Shell (isMenuActive,
  // decide o que destacar no menu), pra nunca divergir entre os dois. Sem
  // isso, dois itens de menu que dividem o mesmo path base (ex.: "Limites de
  // proteção" e "Auto-Exclusão", ambos /user/account-protection com
  // ?section= diferente) não davam pra distinguir só pelo path.
  function getScreenForPath(fullPath: string): string | null {
    const [rawPath, query = ''] = fullPath.split('?');
    const path = rawPath.replace(/\/+$/, '') || '/';
    const target = USER_V2_PATH_SCREENS[path];

    if (!target) {
      return null;
    }

    const section = new URLSearchParams(query).get('section');

    return (section && target.sectionScreens?.[section]) || target.screen;
  }

  // Abre a tela correspondente a uma rota /user/*. Retorna false se a rota
  // não tem tela no modal (aí a navegação normal deve seguir).
  function openByPath(fullPath: string): boolean {
    const [rawPath, query = ''] = fullPath.split('?');
    const path = rawPath.replace(/\/+$/, '') || '/';
    const target = USER_V2_PATH_SCREENS[path];

    if (!target) {
      return false;
    }

    const searchParams = new URLSearchParams(query);
    const section = searchParams.get('section');

    // ?section= normalmente só vira prop da tela padrão (ex.: account-
    // protection abre com o accordion certo já expandido). Mas se bater com
    // uma chave de sectionScreens, desvia pra outra tela inteira (ex.:
    // ?section=self-exclusion abre o card dedicado em vez do accordion) —
    // nesse caso o section já foi consumido pra escolher a tela, não repassa
    // como prop.
    const sectionScreen = section && target.sectionScreens?.[section];
    const screen = sectionScreen || target.screen;

    const props: Record<string, unknown> = { ...(target.props || {}) };

    if (section && !sectionScreen) {
      props.section = section;
    }

    // ?filter= — deep link pra uma tab sem rota dedicada (ex.: Depósito/Saque
    // no histórico de transações; o histórico de apostas já tem rota própria
    // por tab e usa `initialTab`, ver HistoryTransactionsList).
    const filter = searchParams.get('filter');

    if (filter) {
      props.filter = filter;
    }

    open(screen, props, path);

    return true;
  }

  // Rota "canônica" da tela aberta — usada pro deep link de ?filter= em
  // HistoryTransactionsList (telas com um único path sem sectionScreens).
  const currentPath = computed<string>(() => {
    if (!state.value.screen) return '';

    const entry = Object.entries(USER_V2_PATH_SCREENS).find(
      ([, target]) => target.screen === state.value.screen && !target.props,
    );

    return entry ? entry[0] : '';
  });

  return {
    state,
    open,
    close,
    openByPath,
    getScreenForPath,
    currentPath,
    syncAddressBar,
  };
}
